import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import {
    AddShare,
    ForceShareRefresh,
    RemoveShare,
    SetShares,
} from 'src/app/components/share/share.actions';
import {
    selectShares,
    selectShouldForceShareRefresh,
} from 'src/app/components/share/share.reducer';
import { ShareCopyResponseDto } from 'src/app/dtos/share-copy-response.dto';
import { AppState } from 'src/app/models/app-state';
import { Collection } from 'src/app/models/collection';
import { Share, ShareItem } from 'src/app/models/share';
import { ShareResourceService } from './share-resource.service';

@Injectable({ providedIn: 'root' })
export class ShareService {
    constructor(
        private shareResourceService: ShareResourceService,
        private store: Store<AppState>
    ) {}

    // #region Get share
    getShares$(): Observable<Share[]> {
        return this.store.select(selectShouldForceShareRefresh).pipe(
            switchMap((shouldForce) => {
                if (shouldForce) {
                    return this.shareResourceService.getSharesForUser().pipe(
                        switchMap((shares) => {
                            this.store.dispatch(new SetShares(shares));
                            this.store.dispatch(new ForceShareRefresh(false));
                            return this.store.pipe(select(selectShares));
                        })
                    );
                } else {
                    return this.store.pipe(select(selectShares));
                }
            })
        );
    }

    getFullShare(shareId: string): Observable<Share> {
        return this.shareResourceService.getShareById(shareId).pipe(
            switchMap((share) => {
                return forkJoin([
                    of(share),
                    this.shareResourceService.getShareContents(shareId),
                ]);
            }),
            switchMap(([share, content]) => {
                share.content = content;
                if (!share) {
                    return throwError(() => new Error('share not found'));
                } else if (share.content.linkedType === 'Event') {
                    return this.getFullRideEventForShare(share);
                } else if (share.content.linkedType === 'Collection') {
                    return this.getFullCollectionForShare(share);
                } else {
                    return of(share);
                }
            })
        );
    }

    // #endregion

    createShare(share: Share): Observable<Share> {
        return this.shareResourceService.create(share).pipe(
            tap((newShare) => {
                this.store.dispatch(new AddShare(newShare));
            })
        );
    }

    reshare(share: Share): Observable<Share> {
        return this.shareResourceService.reshare(share);
    }

    setShareInactive(share: Share): Observable<Share> {
        const shareCopy = share.clone();
        shareCopy.isActive = false;

        return this.shareResourceService.update(shareCopy).pipe(
            tap(() => {
                this.store.dispatch(new RemoveShare(share.id));
            })
        );
    }

    copyToAccount(
        shareId: string,
        shareItems: ShareItem[]
    ): Observable<ShareCopyResponseDto[]> {
        return this.shareResourceService.copyToAccount(shareId, shareItems);
    }

    private getFullRideEventForShare(share: Share): Observable<Share> {
        const rideEventId = share.content.event.id;
        return this.shareResourceService
            .getSharedRideEventContents(share.id, rideEventId)
            .pipe(
                switchMap((contentCollection) => {
                    share.content.event.setContentCollection(contentCollection);
                    return this.getContentsForAllRideEventCollections(
                        share.id,
                        rideEventId,
                        share.content.event.collections
                    );
                }),
                map((collections) => {
                    share.content.event.collections = collections;
                    return share;
                })
            );
    }

    private getContentsForAllRideEventCollections(
        shareId: string,
        rideEventId: string,
        collections: readonly Collection[]
    ): Observable<Collection[]> {
        if (!collections || collections.length === 0) {
            return of([]);
        }

        return forkJoin(
            collections.map((collection) => {
                return this.getContentsForRideEventCollection(
                    shareId,
                    rideEventId,
                    collection
                );
            })
        );
    }

    private getContentsForRideEventCollection(
        shareId: string,
        rideEventId: string,
        collection: Collection
    ) {
        return this.shareResourceService
            .getSharedRideEventCollectionContents(
                shareId,
                rideEventId,
                collection.id
            )
            .pipe(
                map((contentCollection) => {
                    const resultCollection = collection.clone();
                    resultCollection.setContentCollection(contentCollection);
                    return resultCollection;
                })
            );
    }

    private getFullCollectionForShare(share: Share): Observable<Share> {
        const collectionId = share.content.collection.id;
        return this.shareResourceService
            .getSharedCollectionContents(share.id, collectionId)
            .pipe(
                map((contentCollection) => {
                    share.content.collection.setContentCollection(
                        contentCollection
                    );
                    return share;
                })
            );
    }
}
